
















































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import AWS from 'aws-sdk';
import * as Plotly from 'plotly.js';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { Photo } from '@/models/photo';

interface PhotoWithAudio extends Photo {
  maxDb?: number;
}

interface AudioItem {
  key: string; // clé S3 de l’audio
  maxDb: number; // niveau max en dB
  date: Date; // date de la photo
  url?: string; // URL signée S3
  photoId: string; // id de la photo liée
}

type AggLevel = 'raw' | 'hour' | 'day' | 'week';

@Component
export default class AudioComponent extends Vue {
  @Prop({ required: true }) readonly camera!: Camera;
  @Prop({ required: true }) readonly project!: Project;
  @Prop({ default: () => [] }) readonly photos!: PhotoWithAudio[];

  private audioItems: AudioItem[] = [];
  private loadingKey: string | null = null;
  private currentPlayingKey: string | null = null;

  // Tri & graphe
  private sortField: 'date' | 'maxDb' = 'date';
  private sortAsc = false; // false => décroissant
  private showChart = true;
  private activePhotoId: string | null = null;

  private aggLevel: AggLevel = 'raw';
  private chartPhotoIds: string[] = []; // pour recolorer correctement

  // Visibilité de l’accordéon
  private audioVisible = false;

  private maWindowRaw = 10; // raw = 10 points
  private maWindowAgg = 3; // agrégé = 3 buckets

  // ✅ Simple Moving Average (moyenne mobile "trailing")
  private movingAverage(values: number[], window: number): Array<number | null> {
    const w = Math.max(1, Math.floor(window));
    const out: Array<number | null> = new Array(values.length).fill(null);

    let sum = 0;
    for (let i = 0; i < values.length; i++) {
      sum += values[i];

      if (i >= w) sum -= values[i - w];
      if (i >= w - 1) out[i] = sum / w;
    }

    return out;
  }

  private get s3(): AWS.S3 {
    return new AWS.S3({ region: 'eu-west-3' });
  }

  private get projectCreatedAtMs(): number | null {
    return this.toMillis((this.project as any)?.createdAt);
  }

  private toMillis(raw: any): number | null {
    if (raw == null) return null;

    if (raw instanceof Date) {
      const t = raw.getTime();
      return Number.isFinite(t) ? t : null;
    }

    // number: seconds or milliseconds
    if (typeof raw === 'number') {
      if (!Number.isFinite(raw)) return null;
      return raw < 1e12 ? raw * 1000 : raw; // < 1e12 => seconds (10 digits)
    }

    if (typeof raw === 'string') {
      const s = raw.trim();

      // numeric string: seconds or milliseconds
      if (/^\d+$/.test(s)) {
        const n = parseInt(s, 10);
        if (!Number.isFinite(n)) return null;
        return n < 1e12 ? n * 1000 : n;
      }

      // ISO date string
      const t = new Date(s).getTime();
      return Number.isFinite(t) ? t : null;
    }

    return null;
  }
  created() {
    console.log('[AudioComponent] created', {
      photosLength: this.photos ? this.photos.length : 'no photos prop',
    });
  }

  mounted() {
    console.log('[AudioComponent] mounted', {
      cameraId: this.camera?.id,
      projectId: this.project?.id,
    });
  }

  private get dateSortLabel(): string {
    return (this.$t('audio.sortByDate') as any) || 'Revenir à l’ordre chronologique';
  }

  private get sortLabel(): string {
    const base = this.$t('audio.sortByLevel') || 'Trier par niveau sonore';
    const dir = this.sortAsc ? '↑' : '↓';
    return `${base} (${dir})`;
  }

  private resetChronological() {
    this.sortField = 'date';

    // Chronologique = ancien -> récent
    this.sortAsc = false;

    // Si tu préfères le comportement initial (récent -> ancien), mets plutôt :
    // this.sortAsc = false;

    this.applySort();
  }

  private scrollToItem(photoId: string) {
    this.$nextTick(() => {
      const list = this.$refs.audioList as HTMLElement | undefined;
      if (!list) return;

      const row = list.querySelector(`[data-photo-id="${photoId}"]`) as HTMLElement | null;
      if (!row) return;

      // Position de la row RELATIVE au conteneur scrollable
      const listRect = list.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const rowTopInList = rowRect.top - listRect.top + list.scrollTop;

      const top = Math.max(0, rowTopInList - list.clientHeight / 2 + row.clientHeight / 2);

      (list as any).scrollTo ? (list as any).scrollTo({ top, behavior: 'smooth' }) : (list.scrollTop = top);
    });
  }

  private setAggLevel(level: AggLevel) {
    this.aggLevel = level;
    if (this.showChart) {
      this.$nextTick(() => this.renderChart());
    }
  }

  private clampDb(db: number): number {
    return Math.max(-60, Math.min(0, db));
  }

  private bucketStart(d: Date, level: AggLevel): Date {
    const x = new Date(d.getTime());

    if (level === 'hour') {
      return new Date(x.getFullYear(), x.getMonth(), x.getDate(), x.getHours(), 0, 0, 0);
    }
    if (level === 'day') {
      return new Date(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0);
    }

    // week = lundi 00:00 (local)
    const startOfDay = new Date(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0);
    const dow = (startOfDay.getDay() + 6) % 7; // lundi=0
    startOfDay.setDate(startOfDay.getDate() - dow);
    return startOfDay;
  }

  private bucketMs(level: AggLevel): number {
    if (level === 'hour') return 60 * 60 * 1000;
    if (level === 'day') return 24 * 60 * 60 * 1000;
    return 7 * 24 * 60 * 60 * 1000;
  }

  private buildAggregatedSeries(items: AudioItem[]) {
    const sorted = [...items].sort((a, b) => a.date.getTime() - b.date.getTime());
    const ms = this.bucketMs(this.aggLevel);

    const buckets = new Map<number, AudioItem[]>();
    for (const it of sorted) {
      const start = this.bucketStart(it.date, this.aggLevel).getTime();
      const arr = buckets.get(start) || [];
      arr.push(it);
      buckets.set(start, arr);
    }

    const keys = [...buckets.keys()].sort((a, b) => a - b);

    const minDb = -60;
    const x: Date[] = [];
    const y: number[] = [];
    const text: string[] = [];
    const customdata: string[] = [];
    const width: number[] = [];

    for (const k of keys) {
      const arr = buckets.get(k)!;

      // sample représentatif = le plus bruyant du bucket
      const peak = arr.reduce((m, cur) => (cur.maxDb > m.maxDb ? cur : m), arr[0]);

      const start = new Date(k);
      const end = new Date(k + ms);
      const center = new Date(k + ms / 2);

      x.push(center);
      y.push(this.clampDb(peak.maxDb) - minDb);
      customdata.push(peak.photoId);
      width.push(ms * 0.8);

      text.push(
        `${start.toLocaleString()} → ${end.toLocaleString()}<br>${peak.maxDb.toFixed(1)} dB<br>n=${arr.length}`
      );
    }

    return { x, y, text, customdata, width, minDb };
  }

  @Watch('photos', { immediate: true, deep: true })
  onPhotosChange() {
    console.log('[AudioComponent] onPhotosChange déclenché', {
      newLength: this.photos?.length ?? 0,
    });

    if (!this.photos || !this.photos.length) {
      this.audioItems = [];
      if (this.showChart) this.renderChart();
      return;
    }

    const getMaxDb = (p: any): number | null => {
      if (typeof p.maxDb === 'number') return p.maxDb;
      if (typeof p.maxDb === 'string') {
        const v = parseFloat(p.maxDb);
        return isNaN(v) ? null : v;
      }
      if (typeof p.max_db === 'number') return p.max_db;
      if (typeof p.max_db === 'string') {
        const v = parseFloat(p.max_db);
        return isNaN(v) ? null : v;
      }
      return null;
    };

    const deviceId = (this.camera as any).serialNumber || (this.camera as any).deviceId || (this.camera as any).id;

    if (!deviceId) {
      console.warn(
        '[AudioComponent] serialNumber/deviceId/id manquant sur la caméra, impossible de construire les clés audio',
        { camera: this.camera }
      );
      this.audioItems = [];
      if (this.showChart) this.renderChart();
      return;
    }

    this.audioItems = this.photos
      .map((p: any) => {
        const photoKey = p.key as string;
        if (!photoKey) return null;

        const maxDb = getMaxDb(p);

        // ✅ Si pas de maxDb => on ne l’affiche pas (ni liste, ni graph)
        if (maxDb === null) return null;

        const fileNameWithExt = photoKey.substring(photoKey.lastIndexOf('/') + 1);
        const timestamp = fileNameWithExt
          .replace('.jpeg', '')
          .replace('.jpg', '')
          .replace('.JPEG', '')
          .replace('.JPG', '');

        const audioKey = `${deviceId}/${timestamp}-audio.m4a`;

        const createdAtMs = this.toMillis((p as any).createdAt);
        if (createdAtMs == null) return null;

        const projectStartMs = this.projectCreatedAtMs;
        if (projectStartMs !== null && createdAtMs < projectStartMs) return null;

        const createdAt = new Date(createdAtMs);

        return {
          key: audioKey,
          maxDb, // ✅ plus de défaut -60
          date: createdAt,
          photoId: p.id,
        } as AudioItem;
      })
      .filter((item): item is AudioItem => item !== null);

    this.sortField = 'date';
    this.sortAsc = false;
    this.applySort();

    console.log('[AudioComponent] audioItems calculés', {
      totalPhotos: this.photos.length,
      audioItems: this.audioItems.length,
      sample: this.audioItems.slice(0, 3),
    });

    if (this.showChart) {
      this.$nextTick(() => this.renderChart());
    }
  }

  @Watch('audioItems', { deep: true })
  onAudioItemsChanged() {
    if (this.showChart) {
      this.$nextTick(() => this.renderChart());
    }
  }

  beforeDestroy() {
    const el = this.$refs.audioChart as any;
    if (el) {
      try {
        Plotly.purge(el);
      } catch (e) {
        // ignore
      }
    }
  }

  private formatDate(date: Date): string {
    return date.toLocaleString();
  }

  private levelPercentage(db: number): number {
    const min = -60;
    const max = 0;
    const clamped = Math.max(min, Math.min(max, db));
    return ((clamped - min) / (max - min)) * 100;
  }

  private applySort() {
    const factor = this.sortAsc ? 1 : -1;
    const field = this.sortField;

    this.audioItems.sort((a, b) => {
      const aVal = field === 'date' ? a.date.getTime() : a.maxDb;
      const bVal = field === 'date' ? b.date.getTime() : b.maxDb;
      return (aVal - bVal) * factor;
    });
  }

  private toggleSort() {
    this.sortField = 'maxDb';
    this.sortAsc = !this.sortAsc;
    this.applySort();
  }

  private toggleChart() {
    this.showChart = !this.showChart;
    if (this.showChart) {
      this.$nextTick(() => this.renderChart());
    } else {
      const el = this.$refs.audioChart as any;
      if (el) {
        try {
          Plotly.purge(el);
        } catch (e) {
          // ignore
        }
      }
    }
  }

  private async renderChart() {
    const el = this.$refs.audioChart as any;
    if (!el) return;

    // Pas de données
    if (!this.audioItems.length) {
      this.chartPhotoIds = [];
      await Plotly.react(el, [], {
        paper_bgcolor: 'transparent',
        plot_bgcolor: 'transparent',
      } as Partial<Plotly.Layout>);
      return;
    }

    // Helpers locaux
    const clampDb = (db: number) => Math.max(-60, Math.min(0, db));

    const bucketMs = (level: 'hour' | 'day' | 'week') => {
      if (level === 'hour') return 60 * 60 * 1000;
      if (level === 'day') return 24 * 60 * 60 * 1000;
      return 7 * 24 * 60 * 60 * 1000;
    };

    const bucketStart = (d: Date, level: 'hour' | 'day' | 'week') => {
      const x = new Date(d.getTime());

      if (level === 'hour') {
        return new Date(x.getFullYear(), x.getMonth(), x.getDate(), x.getHours(), 0, 0, 0);
      }
      if (level === 'day') {
        return new Date(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0);
      }

      // week = lundi 00:00 en local
      const startOfDay = new Date(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0);
      const dow = (startOfDay.getDay() + 6) % 7; // lundi=0
      startOfDay.setDate(startOfDay.getDate() - dow);
      return startOfDay;
    };

    // Tri date (base commune)
    const sorted = [...this.audioItems].sort((a, b) => a.date.getTime() - b.date.getTime());

    const level: 'raw' | 'hour' | 'day' | 'week' = (this.aggLevel as any) || 'raw';
    const minDb = -60;

    // Séries finales Plotly
    let x: Date[] = [];
    let y: number[] = [];
    let text: string[] = [];
    let customdata: string[] = [];
    let widths: number[] | undefined = undefined;
    let dbValues: number[] = [];

    if (level === 'raw') {
      x = sorted.map((i) => i.date);

      dbValues = sorted.map((i) => clampDb(i.maxDb)); // ✅ dB clampés
      y = dbValues.map((db) => db - minDb);

      customdata = sorted.map((i) => i.photoId);
      text = sorted.map((i) => `${this.formatDate(i.date)}<br>${clampDb(i.maxDb).toFixed(1)} dB`);
    } else {
      const ms = bucketMs(level);

      const buckets = new Map<number, AudioItem[]>();
      for (const it of sorted) {
        const start = bucketStart(it.date, level).getTime();
        const arr = buckets.get(start) || [];
        arr.push(it);
        buckets.set(start, arr);
      }

      const keys = [...buckets.keys()].sort((a, b) => a - b);

      const _x: Date[] = [];
      const _y: number[] = [];
      const _text: string[] = [];
      const _custom: string[] = [];
      const _w: number[] = [];

      for (const k of keys) {
        const arr = buckets.get(k)!;

        // Représentant du bucket = le plus bruyant
        const peak = arr.reduce((m, cur) => (cur.maxDb > m.maxDb ? cur : m), arr[0]);

        const start = new Date(k);
        const end = new Date(k + ms);
        const center = new Date(k + ms / 2);

        const peakDb = clampDb(peak.maxDb);

        _x.push(center);
        _y.push(peakDb - minDb);
        _custom.push(peak.photoId);
        _w.push(ms * 0.8);

        dbValues.push(peakDb);

        _text.push(
          `${start.toLocaleString()} → ${end.toLocaleString()}<br>` + `${peakDb.toFixed(1)} dB<br>` + `n=${arr.length}`
        );
      }

      x = _x;
      y = _y;
      text = _text;
      customdata = _custom;
      widths = _w;
    }

    // Pour recolorer ensuite via updateChartColors()
    this.chartPhotoIds = [...customdata];

    // Couleurs (barre sélectionnée)
    const colors = customdata.map((pid) => (pid === this.activePhotoId ? '#5fc9e4' : '#4dffcd'));

    const trace: Partial<Plotly.PlotData> = {
      x: x as any,
      y,
      type: 'bar',
      text,
      customdata: customdata as any,
      hovertemplate: '%{text}<extra></extra>',
      marker: { color: colors },
      width: widths as any, // undefined en raw => Plotly choisit
    };

    const maWindow = level === 'raw' ? this.maWindowRaw : this.maWindowAgg;
    const maDb = this.movingAverage(dbValues, maWindow);
    const maY = maDb.map((v) => (v == null ? null : v - minDb));

    const traceMA: Partial<Plotly.PlotData> = {
      x: x as any,
      y: maY as any,
      type: 'scatter',
      mode: 'lines',
      name: `Moyenne mobile (${maWindow})`,
      hovertemplate: `Moyenne mobile (${maWindow})<br>` + `%{x|%d/%m/%Y %H:%M}<br>` + `%{y:.1f} dB<extra></extra>`,
      // Optionnel: si tu veux imposer une couleur cohérente avec ton thème
      line: { color: '#5fc9e4', width: 2 },
    };

    // Axe Y affiché en dB (-60..0) alors que les données sont 0..60
    const tickVals = [0, 10, 20, 30, 40, 50, 60];
    const tickText = tickVals.map((v) => String(v + minDb)); // "-60" .. "0"

    const layout: Partial<Plotly.Layout> = {
      xaxis: {
        type: 'date',
        color: '#5fc9e4',
        gridcolor: '#6c757d',
      },
      yaxis: {
        title: 'dB',
        range: [0, 60],
        tickvals: tickVals,
        ticktext: tickText,
        color: '#5fc9e4',
        gridcolor: '#6c757d',
      },
      bargap: 0.02,
      autosize: true,
      margin: { l: 40, r: 10, b: 30, t: 10, pad: 0 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      showlegend: true,
      legend: {
        orientation: 'h',
        y: 1.15,
        x: 0,
      },
    };

    const config: Partial<Plotly.Config> = {
      responsive: true,
      displaylogo: false,
      scrollZoom: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['lasso2d', 'select2d'] as any,
    };

    await Plotly.react(el, [trace, traceMA], layout, config);

    // Click barre => sélection + scroll sur la liste
    el.removeAllListeners && el.removeAllListeners('plotly_click');
    el.on('plotly_click', (ev: any) => {
      const p = ev.points && ev.points[0];
      if (!p) return;

      // ✅ Ne réagit qu'au trace des barres (curveNumber 0)
      if (p.curveNumber !== 0) return;

      const photoId = p.customdata as string;
      const item = this.audioItems.find((it) => it.photoId === photoId);
      if (item) this.goToPhoto(item, { scroll: true });
    });
  }

  private updateChartColors() {
    const el = this.$refs.audioChart as any;
    if (!el || !this.showChart || !this.chartPhotoIds.length) return;

    const colors = this.chartPhotoIds.map((pid) => (pid === this.activePhotoId ? '#5fc9e4' : '#4dffcd'));

    Plotly.restyle(el, { 'marker.color': [colors] }, [0]);
  }

  private async ensureUrl(item: AudioItem): Promise<void> {
    if (item.url) return;

    this.loadingKey = item.key;
    try {
      const bucketName = 'erige-camera-inbox-prod';

      item.url = this.s3.getSignedUrl('getObject', {
        Bucket: bucketName,
        Key: item.key,
        Expires: 3600,
      });

      this.audioItems = [...this.audioItems];
    } catch (error) {
      console.error('Error generating audio URL', error);
    } finally {
      this.loadingKey = null;
    }
  }

  private async play(item: AudioItem) {
    const audio = this.$refs.audio as HTMLAudioElement | undefined;
    if (!audio) return;

    if (this.currentPlayingKey === item.key) {
      audio.pause();
      this.currentPlayingKey = null;
      return;
    }

    await this.ensureUrl(item);
    if (!item.url) return;

    audio.src = item.url;
    try {
      await audio.play();
      this.currentPlayingKey = item.key;
    } catch (error) {
      console.error('Error playing audio', error);
    }
  }

  private onEnded() {
    this.currentPlayingKey = null;
  }

  private goToPhoto(item: AudioItem, opts: { scroll?: boolean } = {}) {
    this.activePhotoId = item.photoId;

    if (opts.scroll) {
      this.scrollToItem(item.photoId);
    }

    this.$emit('go-to-photo', {
      photoId: item.photoId,
      date: item.date,
      cameraId: this.camera.id,
      projectId: this.project.id,
    });
  }

  @Watch('activePhotoId')
  onActivePhotoIdChanged() {
    this.$nextTick(() => this.updateChartColors());
  }

  @Watch('audioVisible')
  onAudioVisibleChanged(v: boolean) {
    if (v && this.showChart && this.audioItems.length) {
      this.$nextTick(() => this.renderChart());
    }
  }
}
